import React, { useCallback, useRef, MutableRefObject, useEffect } from "react";
import classNames from "classnames";
import { Virtuoso } from "react-virtuoso";
import { m, LazyMotion, domAnimation } from "framer-motion";

import { PaginatedQuestion } from "../../../../../../../../types/working-model";
import { QFilters } from "../../hooks/question-prompts.hooks";
import QuestionCard from "./question-card";

type QuestionListProps = {
	filteredQuestions: PaginatedQuestion[];
	hasMoreRef: React.MutableRefObject<boolean>;
	next: () => void;
	approvalRequired: boolean | undefined;
	selectedFilter: QFilters;
	selectedQuestions: number[];
	setSelectedQuestions: React.Dispatch<React.SetStateAction<number[]>>;
	selecting: boolean;
	isCommentBox?: boolean;
};

// Large enough that firstItemIndex never reaches 0 during normal use.
const PREPEND_BUFFER = 1_000_000;

const QuestionList = ({
	filteredQuestions,
	hasMoreRef,
	next = () => null,
	approvalRequired,
	selectedFilter,
	selectedQuestions,
	setSelectedQuestions,
	selecting,
	isCommentBox,
}: QuestionListProps) => {
	// Compute firstItemIndex synchronously during render so Virtuoso receives the
	// correct value in the same pass as the new data — no second render, no flash.
	const firstItemIndexRef = useRef(PREPEND_BUFFER);
	const prevFirstIdRef = useRef<number | undefined>(undefined);
	const prevFilterRef = useRef(selectedFilter);

	useEffect(() => {
		// Filter changed — fresh list, reset the buffer.
		firstItemIndexRef.current = PREPEND_BUFFER;
		prevFirstIdRef.current = undefined;
		prevFilterRef.current = selectedFilter;
	}, [selectedFilter]);

	const currentFirstId = filteredQuestions[0]?.id;
	if (filteredQuestions.length === 0) {
		firstItemIndexRef.current = PREPEND_BUFFER;
		prevFirstIdRef.current = undefined;
	} else if (prevFirstIdRef.current !== undefined && currentFirstId !== prevFirstIdRef.current) {
		const prependedCount = filteredQuestions.findIndex(q => q.id === prevFirstIdRef.current);
		firstItemIndexRef.current = prependedCount > 0
			? firstItemIndexRef.current - prependedCount
			: PREPEND_BUFFER;
	}
	prevFirstIdRef.current = currentFirstId;

	const rowRenderer = useCallback((_index: number, question: PaginatedQuestion) => {
		return (
			<div>
				<QuestionCard
					question={question}
					approvalRequired={approvalRequired}
					filter={selectedFilter}
					selectedQuestions={selectedQuestions}
					setSelectedQuestions={setSelectedQuestions}
					selecting={selecting}
					isCommentBox={isCommentBox}
				/>
			</div>
		);
	}, [approvalRequired, isCommentBox, selectedFilter, selectedQuestions, selecting, setSelectedQuestions]);

	// Keep a stable renderer reference so re-renders from parent don't force
	// Virtuoso to remount every row.
	const rowRendererRef = useRef(rowRenderer) as MutableRefObject<typeof rowRenderer>;
	rowRendererRef.current = rowRenderer;
	const stableRowRenderer = useCallback((idx: number, qn: PaginatedQuestion) => rowRendererRef.current(idx, qn), []);

	return (
		<LazyMotion features={domAnimation}>
			<m.div
				className={classNames('questions-prompt-list', { selecting: selecting && selectedQuestions.length })}
				initial={{ opacity: 0, x: -50 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ type: 'spring', stiffness: 260, damping: 30 }}
			>
				<Virtuoso
					/* change key to reset virtuoso internals when the filter changes so we can reset scroll behavior otherwise atBottomStateChanged won't fire correctly */
					key={prevFilterRef.current}
					data={filteredQuestions}
					computeItemKey={(_, qn) => qn.id}
					firstItemIndex={firstItemIndexRef.current}
					/*
						items within 500px of the viewport stay mounted, reducing how frequently cards unmount for normal scrolling.
						This helps with maintaining card state (within a reasonable scroll).
					*/
					overscan={500}
					followOutput={false}
					atBottomThreshold={240}
					atBottomStateChange={atBottom => {
						if (atBottom && hasMoreRef.current) {
							next();
						}
					}}
					itemContent={stableRowRenderer}
				/>
			</m.div>
		</LazyMotion>
	);
};

export default QuestionList;
